package dev.kuml.portal.web.api

/**
 * Generic result of a plain CRUD/auth call. Deliberately NOT collapsing status codes into a single
 * boolean (see `build.gradle.kts`'s architecture-decision KDoc) -- [HttpError.status] stays
 * available for callers that branch on the specific code (e.g. a `409` on register).
 */
public sealed interface ApiResult<out T> {
    public data class Success<T>(
        val value: T,
    ) : ApiResult<T>

    /** @param retryAfterSeconds parsed `Retry-After` header, only populated when [status] is `429`
     *   (see `PortalApi.publicJsonRequest`); `null` for every other status. */
    public data class HttpError(
        val status: Int,
        val error: ErrorResponse?,
        val retryAfterSeconds: Long? = null,
    ) : ApiResult<Nothing>

    public data object NetworkError : ApiResult<Nothing>
}

/**
 * Result of `GET /api/projects/{id}/render`. `200` vs `204` is the load-bearing distinction for
 * mustFix 1's first-run detection -- see [Rendered]/[Empty] and `editor.GenerateState`.
 */
public sealed interface RenderResult {
    /**
     * `200` -- [svgBlobUrl] is a `URL.createObjectURL` blob URL, ready for an `<img src=...>`.
     * Caller owns its lifecycle (revoke the previous one before replacing it, see `DiagramStage`).
     *
     * @property svg The same SVG bytes [svgBlobUrl] was created from, as plain text (R-27) --
     *   `EditorScreen.kt`'s `showDiagram` feeds this to `SvgInliner.inline` to attempt an
     *   interactive, click-to-select `<svg>` display instead of the non-interactive `<img>`
     *   [svgBlobUrl] alone can provide. Carried alongside [svgBlobUrl] rather than having the
     *   caller re-fetch the blob URL's own content (`fetch(blobUrl).then { it.text() }`) --
     *   `PortalApi.renderProject` already holds this exact string in hand (it is what
     *   [dev.kuml.portal.web.api.PortalApi.toSvgBlobUrl] was built FROM), so passing it through
     *   costs nothing extra over the network.
     */
    public data class Rendered(
        val svgBlobUrl: String,
        val svg: String,
    ) : RenderResult

    /** `204` -- the project has no `dsl_source` yet; no diagram was ever generated. */
    public data object Empty : RenderResult

    /** `422` -- the persisted DSL no longer renders (should be rare; the DSL was valid when saved). */
    public data class ValidationFailed(
        val message: String?,
    ) : RenderResult

    public data class HttpError(
        val status: Int,
        val error: ErrorResponse?,
    ) : RenderResult

    public data object NetworkError : RenderResult
}

/**
 * Result of `GET /api/diagrams/{diagramId}/dsl` (R-24). `200` vs `204` is the load-bearing
 * distinction "DSL vorhanden" vs "fuer dieses Diagramm wurde noch nie eine DSL gespeichert" --
 * see `editor.DslPanelState`.
 */
public sealed interface DslResult {
    public data class Loaded(
        val dsl: String,
    ) : DslResult

    public data object Empty : DslResult

    public data class HttpError(
        val status: Int,
        val error: ErrorResponse?,
    ) : DslResult

    public data object NetworkError : DslResult
}

/** The `X-AI-Monthly-Usage: used/limit` header on a successful generation, if the caller's plan
 * has an applicable limit. */
public data class AiUsageHeader(
    val used: Int,
    val limit: Int,
)

/**
 * Result of `GET /api/share/{token}/svg` (R-21) -- the PUBLIC, unauthenticated sibling of
 * [RenderResult]. Deliberately a SEPARATE sealed type (not a reuse of [RenderResult]): a share
 * link's `/svg` can additionally be locked behind a password ([PasswordRequired], `401`), a state
 * [RenderResult] has no equivalent for.
 */
public sealed interface ShareSvgResult {
    public data class Rendered(
        val svgBlobUrl: String,
    ) : ShareSvgResult

    /** `204` -- the diagram behind this link currently has no content. */
    public data object Empty : ShareSvgResult

    /** `401` with `code = "share_password_required"` -- the caller must `POST .../unlock` first. */
    public data object PasswordRequired : ShareSvgResult

    /** `422` -- the diagram's stored DSL no longer renders. */
    public data class ValidationFailed(
        val message: String?,
    ) : ShareSvgResult

    /** `404` -- the link is unknown, expired, or was revoked. */
    public data object NotFound : ShareSvgResult

    public data class HttpError(
        val status: Int,
        val error: ErrorResponse?,
        val retryAfterSeconds: Long? = null,
    ) : ShareSvgResult

    public data object NetworkError : ShareSvgResult
}

/** Result of `POST /api/projects/{id}/ai/generate`. */
public sealed interface GenerateResult {
    /** @param generationId R-10's history-row id, or `null` if the server-side history write
     * failed (the generation itself still succeeded). A `null` value hides the "report as
     * incorrect" affordance. */
    public data class Success(
        val svgBlobUrl: String,
        val dsl: String,
        val aiUsage: AiUsageHeader?,
        val generationId: String? = null,
    ) : GenerateResult

    /** @param retryAfterSeconds parsed `Retry-After`, only meaningful when [status] is `429`. */
    public data class HttpError(
        val status: Int,
        val error: ErrorResponse?,
        val retryAfterSeconds: Long? = null,
    ) : GenerateResult

    public data object NetworkError : GenerateResult
}
